import React, { useContext, useEffect, useState } from "react";
import Alert from ".";
import { getIsOnpremise, getIsPatientDeletionEnabled } from "../../utils";
import { useStoreState } from "pullstate";
import Store, { IStore } from "../../utils/Store";
import { get, isEmpty } from "lodash";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { PatientsContext } from "../Patients/PatientsContext";
import { LicenseContext } from "../LicenseManager/LicenseContext";
import { useTranslation } from "react-i18next";
import { LicenseContextType } from "../../types/license-context";
import { useAuth } from "@qure/qure-auth";
import { trackEvent } from "../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";

function Warning() {
  const { wsData } = useContext<any>(PatientsContext);
  const { stickyAlert, alertDetails, trialLicenseDetails } =
    useContext<LicenseContextType>(LicenseContext);
  const isOnpremise = getIsOnpremise();
  const isPatientDeletionEnabled = getIsPatientDeletionEnabled();
  const { t } = useTranslation();
  const { userInfo } = useAuth();
  const isLoggedin = get(userInfo, ["authenticated"], false);
  const {
    isDiskSpaceWarning,
    patientsFileDeletionStatus,
  }: {
    isDiskSpaceWarning: boolean;
    patientsFileDeletionStatus: string;
  } = useStoreState(Store, (store: IStore) => ({
    isDiskSpaceWarning: store.isDiskSpaceWarning,
    patientsFileDeletionStatus: store.patientsFileDeletionStatus,
  }));

  const diskUsage = get(userInfo, ["disk_usage"], {});

  const isDeletionStatus = (status: string) => ["in_process", "started"].includes(status);
  const isFilesDeletionOn =
    isDeletionStatus(diskUsage?.file_deletion) ||
    isDeletionStatus(patientsFileDeletionStatus);

  const onClickDiskAlert = (type: String) => {
    // add code to track event on mixpanel, add event name as DISK_SPACE_ALERT_CLICKED and type in properties
    trackEvent(MIXPANEL_EVENTS.DISK_SPACE_ALERT_CLICKED, {
      type,
    });

    Store.update((s: IStore) => {
      s.isSettingsOpen = true;
      s.selectedSetting = "disk-management";
    });
  };

  const onClickLicenseAlert = (type: string) => {
    trackEvent(MIXPANEL_EVENTS.LICENSE_ALERT_CLICKED, {
      type,
    });

    Store.update((s: IStore) => {
      s.isSettingsOpen = true;
      s.selectedSetting = "license";
    });
  };

  const diskAlertContent = () => {
    if (!isOnpremise || !isPatientDeletionEnabled) return null;

    if (isFilesDeletionOn && patientsFileDeletionStatus !== "completed") {
      return (
        <Alert
          className="bg-red-800"
          messageKey="disk_management.deletion_message"
          showProgress={true}
        />
      );
    }

    if (diskUsage?.used_percent > 99) {
      return (
        <Alert
          className="bg-red-600"
          icon={faTriangleExclamation}
          messageKey="disk_management.popup_message_critical"
          onClick={() => onClickDiskAlert("critical")}
        />
      );
    }

    if (isDiskSpaceWarning) {
      return (
        <Alert
          className="bg-yellow-400 text-gray-900"
          icon={faTriangleExclamation}
          onClick={() => onClickDiskAlert("warning")}
          messageKey="disk_management.popup_message_warning"
          dynamicMessagePart={{
            usedPercent: diskUsage?.used_percent,
            totalDisk: diskUsage?.total,
          }}
        />
      );
    }

    return null;
  };

  const licenseExpiredContent = () => {
    if (isLoggedin && !isEmpty(alertDetails) && alertDetails?.isExpired) {
      return (
        <Alert
          className="bg-red-600 cursor-pointer"
          icon={faTriangleExclamation}
          messageKey="license_expire.popup_message"
          onClick={() => onClickLicenseAlert("expired")}
        />
      );
    }
  };

  const licenseGracePeriodContent = () => {
    if (isLoggedin && !isEmpty(alertDetails) && alertDetails?.in_grace_period) {
      return (
        <Alert
          className="bg-red-700 text-gray-100 cursor-pointer"
          icon={faTriangleExclamation}
          messageKey={t("license_manager.license_grace_period_message", {
            remaining: alertDetails?.grace_days_remaining,
          })}
          onClick={() => onClickLicenseAlert("grace_period")}
        />
      );
    }
  };

  const licenseAlertContent = () => {
    if (!isLoggedin || isEmpty(stickyAlert)) return null;

    const { total_days_remaining, total_scans_remaining, type } = stickyAlert || {};
    const remaining =
      type === "days" ? `${total_days_remaining}` : `${total_scans_remaining}`;

    let message = t("license_manager.license_expire_after", { remaining });
    if (type === "days") {
      message = t("license_manager.license_expire_in", { remaining });
    }

    return (
      <Alert
        className="bg-yellow-400 text-gray-800 cursor-pointer"
        icon={faTriangleExclamation}
        messageKey={message}
        onClick={() => onClickLicenseAlert("regular")}
      />
    );
  };

  const trialLicenseAlertContent = () => {
    if (isLoggedin && !isEmpty(trialLicenseDetails)) {
      const message = t(`demo_version_message`, {
        scans_count: trialLicenseDetails?.total_scans_remaining,
      });
      return (
        <Alert
          className="bg-yellow-800 cursor-pointer"
          messageKey={message}
          onClick={() => onClickLicenseAlert("trial")}
        />
      );
    }
  };

  return (
    diskAlertContent() ||
    trialLicenseAlertContent() ||
    licenseExpiredContent() ||
    licenseGracePeriodContent() ||
    licenseAlertContent()
  );
}

export default Warning;
